<template>
    <transition name="fade" mode="out-in">
        <AppPlaceholder v-if="!order" key="no-order"/>
        <div v-else class="container" key="order">
            <div class="row d-print-none">
              <div class="col-12 mb-4 d-sm-flex justify-content-sm-between align-items-sm-baseline">
                    <div class="pl-3">
                        <h4>{{ isNew ? `New ${orderType}` : orderTitle }} {{ order.number }}</h4>
                        <p class="mb-0 text-muted text-capitalize">
                            <small>{{ orderType }}</small>
                        </p>
                        <p class="mb-0 text-muted" v-if="!isNew">
                            <small>Created on {{ order.created_at | date }}</small>
                        </p>
                        <p class="mb-0 text-muted" v-if="hasInvoiceSent">
                            <small
                            >{{ orderTitle }} not editable, as an invoice
                                <router-link
                                        :to="{
                    name: 'invoice',
                    params: { id: order.invoice.id }
                  }"
                                >
                                    {{ order.invoice.number }}
                                </router-link>
                                <br/>
                                was sent
                                <span v-if="order.invoice.sent_to">
                  on {{ order.invoice.sent_at | date }}<br/>
                  to {{ order.invoice.sent_to }},
                </span>
                            </small>
                        </p>
                    </div>
                    <div class="d-sm-flex col-12 col-sm-auto text-right pr-0 mt-2 mt-sm-0">
                        <button
                                class="btn btn-primary mr-2"
                                :disabled="loading || hasInvoiceSent"
                                @click="save"
                        >
                            Save
                        </button>
                        <button
                                class="btn btn-outline-secondary"
                                :disabled="loading || hasInvoiceSent"
                                @click="saveAndClose"
                        >
                            Save & close
                        </button>
                        <b-dropdown variant="link" no-caret right v-if="!isNew">
                            <template v-slot:button-content>
                                <i class="material-icons">more_vert</i>
                            </template>
                            <b-dropdown-item :disabled="loading" @click="openSendOrderModal"
                            >Send
                            </b-dropdown-item>
                            <b-dropdown-item
                                    v-if="canGenerateInvoice"
                                    :disabled="loading"
                                    @click="generateInvoice"
                            >
                                Generate invoice
                            </b-dropdown-item>
                            <b-dropdown-item :disabled="loading" @click="getOrderPdf">
                                Export to PDF
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </div>

            <div class="alert alert-info" v-if="generatingPdf">
                <strong>Generating PDF</strong>
                <br><span>Loading...</span>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <p class="col-12 text-muted mb-3"><strong>General Info</strong></p>
                    </div>
                    <div class="row">
                        <AppInput
                                :value="order.number"
                                :label="orderNoLabel"
                                field="number"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                @input="updateOrderProps({ number: $event })"
                                class="col-lg-4 col-md-6"
                        />
                        <AppInput
                                v-if="!isAdjustment"
                                :value="order.reference_no"
                                label="Reference number"
                                field="reference_no"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                @input="updateOrderProps({ reference_no: $event })"
                                class="col-lg-4 col-md-6"
                        />
                        <div class="col-lg-4 col-md-6"
                             v-if="!isAdjustment">
                            <label>Exclude order from stock</label>
                            <AppCheckbox
                                    :errors="errors"
                                    label="Exclude"
                                    :disabled="hasInvoiceSent"
                                    :value="order.is_historical"
                                    @input="updateOrderProps({ is_historical: $event })"
                                    field="is_historical"
                            />
                        </div>
                    </div>
                    <div class="row">
                        <AppDatePicker
                                :value="order.issued_at"
                                @input="updateOrderProps({ issued_at: $event })"
                                field="issued_at"
                                :disabled="hasInvoiceSent"
                                label="Date"
                                :errors="errors"
                                class="col-lg-4 col-md-6"
                        />
                        <AppSelect
                                :value="order.status"
                                field="status_code"
                                :errors="errors"
                                :disabled="hasInvoiceSent"
                                label="Status"
                                :options="formattedOrderStatuses"
                                label-field="name"
                                placeholder=""
                                @input="
                updateOrderProps({ status_code: $event ? $event.code : null, status: $event })
              "
                                class="col-lg-3 col-md-6"
                        />
                        <AppSelect
                                :value="order.currency"
                                :options="currencies"
                                track-by="code"
                                label-field="code"
                                field="currency_code"
                                :errors="errors"
                                label="Currency"
                                :disabled="!isNew"
                                placeholder=""
                                @input="
                updateOrderProps({
                  currency: $event,
                  currency_code: $event ? $event.code : null
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                    </div>
                    <div class="row"
                         v-if="!isAdjustment">
                        <p class="col-12 text-muted mb-3"><strong>Parties</strong></p>
                    </div>
                    <div class="row" v-if="isPurchase">
                        <AppSelect
                                :value="order.supplier_company"
                                :errors="errors"
                                :options="companies"
                                :allow-empty="true"
                                field="supplier_company_id"
                                track-by="id"
                                :disabled="hasInvoiceSent"
                                label-field="name"
                                label="Supplier company"
                                placeholder=""
                                @search-change="suggestCompaniesByType($event, 'supplier')"
                                @input="
                updateOrderProps({
                  supplier_company_id: $event ? $event.id : null,
                  supplier_company: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <div class="col-lg-4 col-md-6">
                            <AppSelect
                                    :value="order.supplier_contact_user"
                                    :errors="errors"
                                    :options="contactUserSuggestions"
                                    :allow-empty="true"
                                    :disabled="hasInvoiceSent"
                                    field="supplier_contact_user"
                                    track-by="id"
                                    label-field="name"
                                    label="Supplier contact person"
                                    placeholder=""
                                    @search-change="
                                    suggestContactUsers($event, order.supplier_company_id)"
                                    @input="
                  updateOrderProps({
                    supplier_contact_user_id: $event ? $event.id : null,
                    supplier_contact_user: $event
                  })
                "
                                    class="mb-0"
                            />
                            <UserContactDetails :user="order.supplier_contact_user"/>
                        </div>
                    </div>
                    <div class="row" v-if="isPurchase || isTransfer">
                        <AppSelect
                                :value="order.logistics_company"
                                :errors="errors"
                                :options="companies"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                field="logistics_company_id"
                                track-by="id"
                                label-field="name"
                                label="Logistics company"
                                placeholder=""
                                @search-change="suggestCompaniesByType($event, 'logistics')"
                                @input="
                updateOrderProps({
                  logistics_company_id: $event ? $event.id : null,
                  logistics_company: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <div class="col-lg-4 col-md-6">
                            <AppSelect
                                    :value="order.logistics_contact_user"
                                    :errors="errors"
                                    :options="contactUserSuggestions"
                                    :allow-empty="true"
                                    :disabled="hasInvoiceSent"
                                    field="logistics_contact_user"
                                    track-by="id"
                                    label-field="name"
                                    label="Logistics contact person"
                                    placeholder=""
                                    @search-change="
                                    suggestContactUsers($event, order.logistics_company_id)"
                                    @input="
                  updateOrderProps({
                    logistics_contact_user_id: $event ? $event.id : null,
                    logistics_contact_user: $event
                  })
                "
                                    class="mb-0"
                            />
                            <UserContactDetails :user="order.logistics_contact_user"/>
                        </div>
                    </div>
                    <div class="row" v-if="isTreatment || isDelivery || isSale">
                        <AppSelect
                                :value="order.client_company"
                                :errors="errors"
                                :options="companies"
                                :allow-empty="true"
                                field="client_company_id"
                                track-by="id"
                                :disabled="hasInvoiceSent"
                                label-field="name"
                                label="Client company"
                                placeholder=""
                                @search-change="suggestCompaniesByType($event, 'client')"
                                @input="
                updateOrderProps({
                  client_company_id: $event ? $event.id : null,
                  client_company: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <div class="col-lg-4 col-md-6">
                            <AppSelect
                                    :value="order.client_contact_user"
                                    :errors="errors"
                                    :options="contactUserSuggestions"
                                    :allow-empty="true"
                                    field="client_contact_user"
                                    track-by="id"
                                    :disabled="hasInvoiceSent"
                                    label-field="name"
                                    label="Client contact person"
                                    placeholder=""
                                    @search-change="
                                    suggestContactUsers($event, order.client_company_id)"
                                    @input="
                  updateOrderProps({
                    client_contact_user_id: $event ? $event.id : null,
                    client_contact_user: $event
                  })
                "
                                    class="mb-0"
                            />
                            <UserContactDetails :user="order.client_contact_user"/>
                        </div>
                    </div>
                    <div class="row">
                        <AppDatePicker
                                :value="order.delivery_due_at"
                                v-if="isPurchase || isTransfer || isDelivery"
                                type="datetime"
                                @input="updateOrderProps({ delivery_due_at: $event })"
                                field="delivery_due_at"
                                label="Delivery due at"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                class="col-lg-4 col-md-6"
                        />
                        <AppSelect
                                :value="order.loading_location"
                                v-if="isPurchase || isTransfer"
                                :errors="errors"
                                :options="locationSuggestions"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                field="loading_location"
                                track-by="id"
                                label-field="name"
                                :preserve-search="false"
                                label="Loading location"
                                placeholder=""
                                @search-change="suggestLocations"
                                @input="
                updateOrderProps({
                  loading_location_id: $event ? $event.id : null,
                  loading_location: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        >
                            <template slot="noResult">
                                <button
                                        @click="addLocation('loading_location')"
                                        class="col-sm-12 btn btn-link"
                                >
                                    Add location
                                </button>
                            </template>
                        </AppSelect>
                        <AppSelect
                                :value="order.loading_sub_location"
                                :multiple="false"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                v-if="order.loading_location"
                                :options="order.loading_location.sub_locations"
                                track-by="id"
                                :preserve-search="false"
                                label-field="location"
                                label="Sub location"
                                placeholder=""
                                @input="
                updateOrderProps({
                  loading_sub_location_id: $event ? $event.id : null,
                  loading_sub_location: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        >
                            <template slot="noResult">
                                <button
                                        @click="
                                        addLocation(
                                        'loading_sub_location',
                                        order.loading_location_id)"
                                        class="col-sm-12 btn btn-link"
                                >
                                    Add sub location
                                </button>
                            </template>
                        </AppSelect>
                    </div>
                    <div class="row" v-if="isTreatment || isDelivery || isSale">
                        <p class="col-12 text-muted mb-3"><strong>Cargo</strong></p>
                    </div>
                    <div class="row">
                        <AppSelect
                                :value="order.vessel"
                                v-if="isTreatment || isDelivery"
                                :errors="errors"
                                :options="vessels"
                                :allow-empty="true"
                                field="job_cargo_vessel"
                                track-by="id"
                                :disabled="hasInvoiceSent"
                                label-field="name"
                                label="Cargo vessel"
                                placeholder="Select vessel"
                                @input="
                updateOrderProps({
                  job_cargo_vessel: $event ? $event.id : null,
                  vessel: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <AppInput
                                :value="order.job_cargo_size_mtvac"
                                v-if="isTreatment || isDelivery"
                                label="Cargo size (mt vac)"
                                field="job_cargo_size_mtvac"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                @input="updateOrderProps({ job_cargo_size_mtvac: $event })"
                                class="col-lg-4 col-md-6"
                        />
                        <AppInput
                                :value="order.job_cargo_grade"
                                v-if="isTreatment || isDelivery"
                                label="Cargo grade"
                                field="job_cargo_grade"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                @input="updateOrderProps({ job_cargo_grade: $event })"
                                class="col-lg-4 col-md-6"
                        />
                    </div>

                    <div class="row" v-if="isTreatment">
                        <p class="col-12 text-muted mb-3"><strong>Job</strong></p>
                    </div>

                    <div class="row">
                        <AppSelect
                                :value="order.job_location"
                                v-if="isTreatment || isDelivery || isSale"
                                :errors="errors"
                                :options="locationSuggestions"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                field="job_location"
                                track-by="id"
                                label-field="name"
                                label="Job location"
                                :preserve-search="false"
                                placeholder=""
                                @search-change="suggestLocations"
                                @input="
                updateOrderProps({
                  job_location_id: $event ? $event.id : null,
                  job_location: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        >
                            <template slot="noResult">
                                <button
                                        @click="addLocation('job_location')"
                                        class="col-sm-12 btn btn-link">
                                    Add location
                                </button>
                            </template>
                        </AppSelect>
                        <AppSelect
                                :value="order.job_sub_location"
                                :multiple="false"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                v-if="order.job_location"
                                :options="order.job_location.sub_locations"
                                track-by="id"
                                :preserve-search="false"
                                label-field="location"
                                label="Sub location"
                                placeholder=""
                                @input="
                updateOrderProps({
                  job_sub_location_id: $event ? $event.id : null,
                  job_sub_location: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        >
                            <template slot="noResult">
                                <button
                                        @click="
                                        addLocation('job_sub_location',
                                        order.job_location_id)"
                                        class="col-sm-12 btn btn-link"
                                >
                                    Add sub location
                                </button>
                            </template>
                        </AppSelect>
                    </div>
                    <div class="row">
                        <AppDatePicker
                                :value="order.job_lading_bill_issued_at"
                                v-if="isTreatment || isDelivery"
                                label="Bill of lading date"
                                field="job_lading_bill_issued_at"
                                :disabled="hasInvoiceSent"
                                :errors="errors"
                                @input="
                updateOrderProps({
                  job_lading_bill_issued_at: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <AppDatePicker
                                :value="order.job_treatment_at"
                                v-if="isTreatment"
                                :allow-empty="true"
                                :disabled="hasInvoiceSent"
                                label="Treatment at"
                                field="job_treatment_at"
                                :errors="errors"
                                @input="
                updateOrderProps({
                  job_treatment_at: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <AppDatePicker
                                :value="order.job_additivation_started_at"
                                v-if="isTreatment"
                                :allow-empty="true"
                                type="datetime"
                                :disabled="hasInvoiceSent"
                                label="Additivation started at"
                                field="job_additivation_started_at"
                                :errors="errors"
                                @input="
                updateOrderProps({
                  job_additivation_started_at: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                        <AppDatePicker
                                :value="order.job_additivation_ended_at"
                                v-if="isTreatment"
                                :allow-empty="true"
                                type="datetime"
                                :disabled="hasInvoiceSent"
                                label="Additivation ended at"
                                field="job_additivation_ended_at"
                                :errors="errors"
                                @input="
                updateOrderProps({
                  job_additivation_ended_at: $event
                })
              "
                                class="col-lg-4 col-md-6"
                        />
                    </div>

                    <div class="row">
                        <AppWysiwyg
                                label="Note"
                                :value="order.notes"
                                field="notes"
                                :errors="errors"
                                :disabled="hasInvoiceSent"
                                @input="updateOrderProps({ notes: $event })"
                                class="col-lg-6"
                        />
                    </div>

                    <div class="row mt-5">
                        <div class="col-12 d-flex justify-content-between align-items-start">
                            <p class="text-muted mb-3"><strong>Order items</strong></p>
                            <div>
                                <button
                                        class="btn btn-sm btn-secondary ml-2"
                                        :disabled="hasInvoiceSent"
                                        @click="addProduct"
                                >
                                    Add product
                                </button>
                                <button
                                        class="btn btn-sm btn-secondary ml-2"
                                        :disabled="hasInvoiceSent"
                                        @click="addService"
                                >
                                    Add service
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <thead>
                            <tr>
                                <th>Product</th>
                                <th>Quantity</th>
                                <th>
                                    Price
                                </th>
                                <th class="text-right">
                                    Subtotal
                                </th>
                                <th class="text-right">Status</th>
                            </tr>
                            </thead>
                            <tbody
                                    v-for="(line, index) in orderLines"
                                    :key="line.id"
                                    :class="{
                  'd-print-none': line.product && line.product.is_hidden
                }"
                            >
                            <tr
                                    class="pointer bg-highlight"
                                    :class="{ 'table-danger': errorInLine(index) }"
                                    @click="editOrderLine({ line: line, index: index })"
                            >
                                <td>
                                    <strong>
                                        {{ line.product ? line.product.name : '-' }}
                                    </strong>
                                </td>
                                <td>{{ line.quantity }} {{ line.unit_type_code || '' }}</td>
                                <td>
                                    {{ line.price | currency(2, order.currency_code) }}
                                </td>
                                <td class="text-right">
                                    {{ (line.price * line.quantity)
                                    | currency(2, order.currency_code) }}
                                </td>
                                <td class="text-right">
                                    <strong v-if="line.status && !isServiceLine(line)">
                                        {{ line.status.name }}
                                    </strong>
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colspan="4" class="font-weight-bold text-right">
                                    Total amount:
                                    {{ totalAmount | currency(2, order.currency_code) }}
                                </td>
                                <td></td>
                            </tr>
                            <tr>
                                <td colspan="4" class="font-weight-bold text-right">
                                    VAT ({{ vatPct }}%):
                                    {{ vatAmount | currency(2, order.currency_code) }}
                                </td>
                                <td></td>
                            </tr>
                            <tr>
                                <td colspan="4" class="font-weight-bold text-right">
                                    Total with VAT:
                                    {{ totalAmountWithVat | currency(2, order.currency_code) }}
                                </td>
                                <td></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
            <SendOrderModal :redirectToAfterClose="redirectToAfterClose"/>
            <OrderLineModal :orderType="orderType"/>
            <LocationModal/>
        </div>
    </transition>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import debounce from 'lodash.debounce';
import download from 'downloadjs';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import AppInput from '../form/AppInput';
import OrderLineModal from './OrderLineModal';
import NotificationService from '../../services/notification.service';
import AppPlaceholder from '../form/AppPlaceholder';
import { formatDate } from '../../filters/date.filter';
import AppSelect from '../form/AppSelect';
import AppDatePicker from '../form/AppDatePicker';
import UserContactDetails from '../users/UserContactDetails';
import formatCurrency from '../../filters/currency.filter';
import AppCheckbox from '../form/AppCheckbox';
import SendOrderModal from './OrderSendModal';
import AppWysiwyg from '../form/AppWysiwyg';
import LocationModal from '../locations/LocationModal';
// import AppError from '../form/AppError';

export default {
  components: {
    AppCheckbox,
    AppSelect,
    AppPlaceholder,
    OrderLineModal,
    AppInput,
    // AppError,
    AppDatePicker,
    BDropdown,
    BDropdownItem,
    UserContactDetails,
    SendOrderModal,
    AppWysiwyg,
    LocationModal,
  },
  data() {
    return {
      redirectToAfterClose: false,
      companies: [],
      contactUserSuggestions: [],
      locationSuggestions: [],
      loading: false,
      generatingPdf: false,
    };
  },
  filters: {
    date: formatDate,
    currency: formatCurrency,
  },
  computed: {
    ...mapGetters({
      order: 'orders/order',
      orderStatuses: 'orders/orderStatuses',
      totalAmount: 'orders/totalAmount',
      vatPct: 'orders/vatPct',
      vatAmount: 'orders/vatAmount',
      totalAmountWithVat: 'orders/totalAmountWithVat',
      orderLines: 'orders/orderLines',
      currencies: 'currencyRates/currencies',
      vessels: 'vessels/vessels',
    }),
    ...mapState({
      errors: (state) => state.orderLines.orderErrors,
      prevStatusCode: (state) => state.orders.prevStatusCode,
      isSendOrderModalOpen: (state) => state.orders.isSendOrderModalOpen,
      isStatusCodeChanged: (state) => state.orders.isStatusCodeChanged,
    }),
    formattedOrderStatuses() {
      const orderStatuses = [...this.orderStatuses];
      if (orderStatuses.length) {
        const transitIndex = orderStatuses.findIndex(
          (orderStatus) => orderStatus.code === 'in_transit',
        );
        const invoicedIndex = orderStatuses.findIndex(
          (orderStatus) => orderStatus.code === 'invoiced',
        );

        if (this.isTreatment || this.isDelivery || this.isSale) {
          orderStatuses[transitIndex].name = 'In Progress';
        } else if (this.isTransfer || this.isPurchase) {
          orderStatuses[transitIndex].name = 'In Transit';
          delete orderStatuses[invoicedIndex];
        }
      }
      return orderStatuses;
    },
    isNew() {
      return this.$route.params.id === 'new';
    },
    hasInvoice() {
      return this.order.has_invoice;
    },
    hasInvoiceSent() {
      return this.order.has_invoice_sent;
    },
    canGenerateInvoice() {
      return !(this.isTransfer || this.isPurchase);
    },
    listType() {
      return this.$route.params.orderType;
    },
    orderType() {
      return this.order.type_code;
    },
    isAdjustment() {
      return this.orderType === 'adjustment';
    },
    isPurchase() {
      return this.orderType === 'purchase';
    },
    isTransfer() {
      return this.orderType === 'transfer';
    },
    isTreatment() {
      return this.orderType === 'treatment';
    },
    isDelivery() {
      return this.orderType === 'delivery';
    },
    isSale() {
      return this.orderType === 'sale';
    },
    orderNoLabel() {
      if (this.isJobOrderType) {
        return 'Job No';
      }
      if (this.isPurchase) {
        return 'Order No';
      }
      if (this.isAdjustment) {
        return 'Adjustment No';
      }
      return 'Transfer No';
    },
    isJobOrderType() {
      return ['treatment', 'delivery', 'sale', 'job'].includes(this.orderType);
    },
    orderTitle() {
      return this.isJobOrderType ? 'Job' : 'Order';
    },
  },
  watch: {
    '$route.params.id'() {
      this.initOrder();
    },
  },
  mounted() {
    this.getCurrencies();
    this.initOrder();
  },
  methods: {
    // Start process of adding new location
    addLocation(field, parentLocationId = null) {
      this.$store.dispatch('locations/openLocationModal', {
        parentLocationId,
        type: 'job',
      })
        .then((res) => {
          this.updateOrderProps({
            [field]: res,
            [`${field}_id`]: res.id,
          });
        });
    },
    isServiceLine(line) {
      return line.product ? line.product.types.filter((type) => type.name === 'service').length : 0;
    },
    errorInLine(lineId) {
      return (
        this.errors.has(`order_lines.${lineId}.product_id`)
        || this.errors.has(`order_lines.${lineId}.quantity`)
        || this.errors.has(`order_lines.${lineId}`)
      );
    },
    getCurrencies() {
      this.$store.dispatch('currencyRates/getCurrencies');
    },
    suggestLocations: debounce(function suggest(query) {
      return this.$store
        .dispatch('locations/suggestLocations', {
          query,
          type: 'job',
        })
        .then((suggestions) => {
          this.locationSuggestions = suggestions;
        });
    }, 300),
    suggestContactUsers: debounce(function suggest(query, companyId) {
      return this.$store
        .dispatch('users/suggestUsers', {
          query,
          company_id: companyId,
        })
        .then((suggestions) => {
          this.contactUserSuggestions = suggestions;
        });
    }, 300),
    suggestCompaniesByType: debounce(function suggest(query, type) {
      const params = {
        query,
        type: type,
      };
      return this.$store.dispatch('companies/suggestCompanies', params)
        .then((res) => {
          this.companies = res;
        });
    }, 300),
    async initOrder() {
      if (this.isNew) {
        this.$store.dispatch('orders/newOrder', this.listType);
      } else if (this.$route.params.id) {
        this.getOrder();
      } else {
        this.$store.commit('orders/orderId', null);
      }
    },
    async generateInvoice() {
      try {
        const res = await this.$store.dispatch('orders/generateInvoice');
        this.$router.push({
          name: 'invoice',
          params: { id: res.id },
        });
      } catch (e) {
        console.log(e);
      }
    },
    getOrderPdf() {
      this.generatingPdf = true;

      this.$store.dispatch('orders/getOrderPdf', this.$route.params.id)
        .then((res) => {
          NotificationService.success('Order PDF generated. Downloading...');
          download(res.file, res.name, 'application/pdf');
        })
        .finally(() => {
          this.generatingPdf = false;
        });
    },
    getOrder() {
      this.loading = true;

      this.$store
        .dispatch('orders/getOrder', this.$route.params.id)
        .then(() => {
          this.$store.commit('orders/prevStatusCode', this.order.status_code);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addProduct() {
      this.$store.dispatch('orderLines/newOrderLine', { line_type: 'regular' });
    },
    addService() {
      this.$store.dispatch('orderLines/newOrderLine', {
        line_type: 'service',
        quantity: 1,
      });
    },
    openSendOrderModal() {
      this.$store.commit('orders/isSendOrderModalOpen', {
        isOpen: true,
      });
    },
    editOrderLine(lineProps) {
      const props = {
        id: lineProps.line.id,
        isNewOrderLine: false,
        line_type: 'regular',
        lineIndex: lineProps.index,
      };

      if (
        lineProps.line.product
        && lineProps.line.product.types
        && lineProps.line.product.types.find((type) => type.id === 'service')
      ) {
        props.line_type = 'service';
      }
      this.$store.dispatch('orderLines/openOrderLineModal', props);
    },
    updateOrderProps(props) {
      if (props.hasOwnProperty('job_location')) {
        this.updateOrderProps({
          job_sub_location_id: null,
          job_sub_location: null,
        });
      }
      this.$store.dispatch('orders/updateOrderProps', props);
    },
    async saveAndClose() {
      try {
        await this.save();
        this.redirectToAfterClose = {
          name: 'orders',
          params: { orderType: this.listType },
        };
        if (!this.isStatusCodeChanged) {
          this.$router.push(this.redirectToAfterClose);
        }
      } catch (e) {
        console.log(e);
      }
    },
    async save() {
      this.loading = true;
      this.errors.clear();
      if (this.prevStatusCode !== this.order.status_code && !this.isNew) {
        this.$store.commit('orders/isSendOrderModalOpen', {
          isOpen: true,
          isStatusCodeChanged: true,
        });
      }
      return this.$store
        .dispatch(this.isNew ? 'orders/saveOrder' : 'orders/updateOrder')
        .then((res) => {
          if (this.isNew) {
            this.$router.push({
              name: 'orders',
              params: {
                orderType: this.listType,
                id: res.order_id,
              },
            });
          }
          NotificationService.success(res.message);
        })
        .catch((err) => {
          this.errors.set(err.response.data.errors);
          throw err;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    destroy() {
      this.$bvModal
        .msgBoxConfirm('Delete order?', {
          okTitle: 'Delete',
          okVariant: 'danger',
        })
        .then((value) => {
          if (value) {
            this.$store.dispatch('orders/deleteOrder')
              .then((res) => {
                NotificationService.success(res.message);
                this.$router.push({ name: 'orders' });
              });
          }
        });
    },
  },
};
</script>
